import React from 'react';
import { Button, Icon } from '@iq/react-components';
import { USER_EVENT_ACTIVITY, USER_EVENT_STATUSES } from '../../../../constants';
import { getGroupedUserEventOptions } from '../utils';

const quickFilterTitle = (props) => (
  <div className="quick-filter-title">
    <label> {props.title}</label>
    <Button
      design="text"
      slim
      icon={
        <Icon
          icon="he-info"
          size="s"
        />
      }
      tooltip={
        "Selecting 'Quick Filter' resets all other filters to default and applies the filter to all events."
      }
      onClick={(e) => e.preventDefault()}
    ></Button>
  </div>
);

const falseAlarmFilterTitle = (props) => (
  <div className="false-alarm-filter-title">
    <label> {props.title}</label>
    <Button
      design="text"
      slim
      icon={
        <Icon
          icon="he-info"
          size="s"
        />
      }
      tooltip={
        'Turning the False alarm filtering on will hide events labeled as "false" from the list and timeline view.'
      }
      onClick={(e) => e.preventDefault()}
    ></Button>
  </div>
);

const getUserEventFiltersSchema = (userEventTypes, tags, users) => {
  const groupedOptions = getGroupedUserEventOptions(userEventTypes);

  const includesTaskTypeEvent = userEventTypes.find((eventType) => !!eventType.task);

  const schema = {
    type: 'object',
    properties: {
      quickFilterTitle: {
        type: 'object',
        title: 'Quick Filter',
      },
      quickFilter: {
        type: 'string',
        default: 'none',
        anyOf: [
          { enum: ['none'], title: 'None' },
          { enum: ['overdue'], title: 'All overdue' },
          { enum: ['todo'], title: 'All To-do' },
        ],
      },
      assignee: {
        type: 'string',
        title: 'Assignee',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select assignee...',
        default: [],
        anyOf: [...users.map((user) => ({ enum: [user.id], title: user.name }))],
      },
      creator: {
        type: 'string',
        title: 'Creator',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select creator...',
        default: [],
        anyOf: [...users.map((user) => ({ enum: [user.id], title: user.name }))],
      },
      type: {
        type: 'string',
        title: 'Event Type',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select types...',
        groupedOptions,
        default: [],
        anyOf: [
          ...userEventTypes.map((type) => ({
            enum: [type.id],
            title: type.name,
          })),
        ],
      },
      activity: {
        type: 'string',
        title: 'Activity',
        placeholder: 'Select activity...',
        default: 'all',
        anyOf: [
          ...USER_EVENT_ACTIVITY.map((activity) => ({
            enum: [activity.toLowerCase()],
            title: activity,
          })),
        ],
      },
      status: {
        type: 'string',
        title: 'Status',
        placeholder: 'Select status...',
        default: 'all',
        anyOf: [
          ...USER_EVENT_STATUSES.map((status) => ({
            enum: [status.toLowerCase()],
            title: status,
          })),
        ],
      },
      tags: {
        title: 'Tags',
        type: 'string',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select tags...',
        default: [],
        anyOf: tags.map((tag) => ({
          enum: [tag.id],
          title: tag.name,
        })),
      },
    },
  };

  if (!includesTaskTypeEvent) {
    delete schema.properties.activity;
  }

  return schema;
};

const iotEventFiltersSchema = (iotEventTypes, pointGroups = []) => {
  const schema = {
    type: 'object',
    properties: {
      eventType: {
        type: 'string',
        title: 'Event Type',
        isMulti: true,
        uniqueItems: true,
        default: [],
        placeholder: 'Select types...',
        anyOf: iotEventTypes.map((iotEventType) => ({
          enum: [iotEventType.id],
          title: iotEventType.name,
        })),
      },
    },
  };

  if (pointGroups.length) {
    schema.properties.remoteSource = {
      type: 'string',
      title: 'Remote Source',
      isMulti: true,
      uniqueItems: true,
      placeholder: 'Select remote sources...',
      default: [],
      anyOf: pointGroups.map((pointGroup) => ({
        enum: [pointGroup],
        title: pointGroup,
      })),
    };
  }
  return schema;
};

const getUserEventFiltersUiSchema = (formData) => ({
  quickFilterTitle: {
    'ui:TitleFieldTemplate': quickFilterTitle,
  },
  quickFilter: {
    'ui:options': { label: false },
  },
  status: {
    'ui:disabled': formData.user?.quickFilter !== 'none',
  },
});

const iotEventFiltersUiSchema = {
  falseAlarmFilterTitle: {
    'ui:TitleFieldTemplate': falseAlarmFilterTitle,
  },
  falseAlarmFilter: {
    'ui:options': { label: false },
  },
  remoteSource: {
    'ui:options': {
      classNames: 'remote-source-select',
    },
    'ui:widget': 'select',
  },
};

export {
  getUserEventFiltersSchema,
  getUserEventFiltersUiSchema,
  iotEventFiltersSchema,
  iotEventFiltersUiSchema,
};
